// @ts-check
import { values, keyBy, forEach, map, groupBy } from "lodash";

import {
  getConstraintColumns,
  getForecastMacroAllocations,
  setGroupingsWithActualConstraints,
  setGroupingsWithDefaultConstraints,
  setGroupingsWithForecastCells,
  setGroupingsWithForecastDefaultCells,
  setCalculatedSumTotals,
  setNotifications,
  pruneEmptyConstraintGroupings,
  buildGroupingLookup,
  buildLineItemConstraintLookups,
  buildCellLookup,
  hashGrouping,
  buildGroupLookup,
} from "../util";

import buildConstraintGroupings from "./buildGroupings/constraintGroupings";
import setGroupingsWithLineItems from "./setLineItems/demandLineItems";

const buildGroupedByConstraints = ({
  rootGroup,
  data,
  hideHiddenTeams,
  groupTypes,
  shouldHash,
}) => {
  const columns = getConstraintColumns(data.allocationProjects);
  const allocationProjectLookup = keyBy(
    map(columns, (c) => c.allocationProject),
    "id"
  );
  const groupLookup = buildGroupLookup({
    macroAllocationGroups: data.macroAllocationGroups,
    relatedBudgetTargetGroups: data.relatedBudgetTargetGroups,
    groupTypes,
  });

  const macroAllocations = getForecastMacroAllocations({
    macroAllocations: data.macroAllocations,
    allocationProjectLookup,
    groupedByDemand: false,
  });
  const constraints = groupBy(data.constraints, "groupId");

  const groupings = buildConstraintGroupings({
    rootGroup: {
      ...rootGroup,
      totalCapacityFte: data.rootGroupTotalCapacityFte,
      directMembersCapacityFte: data.rootGroupDirectMembersCapacityFte,
    },
    groupLookup,
    columnCount: columns.length,
    hideHiddenTeams,
    groupTypes,
  });
  setGroupingsWithActualConstraints({
    groupings,
    constraints,
    allocationProjectLookup,
    columns,
  });
  setGroupingsWithDefaultConstraints({
    groupings,
    allocationProjectLookup,
    columns,
  });

  // to work out forecast totals we need line items & forecast cells (they are not shown in the UI)
  setGroupingsWithLineItems({
    groupings,
    macroAllocations,
    columnCount: columns.length,
    groupLookup,
    groupTypes,
    hideHiddenTeams,
  });
  setGroupingsWithForecastCells({
    groupings,
    macroAllocations,
    columns,
    groupedByDemand: false,
    rootGroup,
  });
  setGroupingsWithForecastDefaultCells(groupings, columns, false, rootGroup);
  setCalculatedSumTotals(groupings, columns);
  setNotifications(groupings, columns);

  pruneEmptyConstraintGroupings(groupings);

  const columnLookup = keyBy(columns, "id");
  const groupingLookup = buildGroupingLookup(groupings, {});
  const { lineItemLookup, constraintCellLookup } =
    buildLineItemConstraintLookups(values(groupingLookup), {}, {});
  const cellLookup = buildCellLookup(values(lineItemLookup), {});

  // Hash groupings in case anything changed
  if (shouldHash) {
    forEach(groupings, hashGrouping);
  }

  return {
    columns,
    groupings,
    // lookup objects: for when we need fast access to objects in the tree
    lookups: {
      allocationProjectLookup,
      columnLookup,
      groupLookup,
      groupingLookup,
      lineItemLookup,
      cellLookup,
      constraintCellLookup,
      macroAllocations,
    },
  };
};

export default buildGroupedByConstraints;
